import { supabase } from "./supabase";
import type { LoggedMeal, MealEstimate, MealType } from "./types";

export interface Favorite {
  id: string;
  name: string;
  meal_type: MealType | null;
  estimate: MealEstimate;
  external_photo_url?: string;
  photo_data_url?: string;
  caption?: string;
  source_providers: string[];
  created_at: string;
}

export async function loadFavorites(): Promise<Favorite[]> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data, error } = await supabase
    .from("favorites")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;

  const favorites: Favorite[] = [];
  for (const row of data ?? []) {
    let photo_data_url: string | undefined;
    if (row.photo_path) {
      const { data: signed } = await supabase.storage
        .from("meal-photos")
        .createSignedUrl(row.photo_path, 60 * 60);
      photo_data_url = signed?.signedUrl;
    } else if (row.external_photo_url) {
      photo_data_url = row.external_photo_url;
    }
    favorites.push({
      id: row.id,
      name: row.name,
      meal_type: row.meal_type,
      estimate: row.estimate,
      external_photo_url: row.external_photo_url ?? undefined,
      photo_data_url,
      caption: row.caption ?? undefined,
      source_providers: row.source_providers ?? [],
      created_at: row.created_at
    });
  }
  return favorites;
}

export async function saveCustomFavorite(input: {
  name: string;
  items: import("./types").FoodItem[];
  meal_type: MealType | null;
}): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");

  const totals = input.items.reduce(
    (a, i) => ({
      calories: a.calories + i.calories,
      protein_g: a.protein_g + i.protein_g,
      carbs_g: a.carbs_g + i.carbs_g,
      fat_g: a.fat_g + i.fat_g,
      fiber_g: a.fiber_g + (i.fiber_g ?? 0)
    }),
    { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 }
  );

  const estimate: MealEstimate = {
    items: input.items,
    totals,
    confidence: "high",
    clarifying_questions: [],
    notes: "Custom recipe — ingredients you set"
  };

  const { error } = await supabase.from("favorites").insert({
    user_id: user.id,
    name: input.name,
    meal_type: input.meal_type,
    estimate,
    source_providers: ["custom"]
  });
  if (error) throw error;
}

export async function saveFavoriteFromMeal(meal: LoggedMeal, name: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");

  // We don't copy the photo to the favorites bucket — favorites reference
  // the meal's external image URL if present, or fall back to emoji at display time.
  const { error } = await supabase.from("favorites").insert({
    user_id: user.id,
    name,
    meal_type: meal.meal_type,
    external_photo_url: meal.photo_data_url?.startsWith("http")
      ? meal.photo_data_url
      : null,
    caption: meal.caption ?? null,
    estimate: meal.estimate,
    source_providers: meal.source_providers
  });
  if (error) throw error;
}

export async function deleteFavorite(id: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase.from("favorites").delete().eq("id", id);
  if (error) throw error;
}
